import { property, remote, supertypeClass } from '@haventech/amorphic';
import { LockedByUserType } from '../interfaces/CoverpathTypes';
import { AppController } from '@hit/amorphic-types';
import { AuthenticatingController } from '../../../common/js/AuthenticatingController';
import { Customer } from '../../../common/js/customer/Customer';
import { Agent } from '../../../common/js/customer/Agent';
import { CasApplication } from '../../classes/coverpath_subclasses/CasApplication';

interface ControllerWithCustomer extends AuthenticatingController {
  customer?: Customer;

  getAgent(): Agent;
}

@supertypeClass({ toClient: true, toServer: true })
export class ApplicationLockController extends AppController {
  @property()
  protected controller: AuthenticatingController;

  constructor() {
    super();
  }

  get customer(): Customer {
    return (<ControllerWithCustomer>this.controller).customer || null;
  }

  getAgent(): Agent {
    return (<ControllerWithCustomer>this.controller).getAgent() || null;
  }

  static new(controller: AuthenticatingController) {
    return new ApplicationLockController().init(controller);
  }

  init(controller: AuthenticatingController) {
    this.controller = controller;
    return this;
  }

  @remote({ on: 'server' })
  public setApplicationLockedBy(userType: LockedByUserType) {
    const application = this.customer.application as CasApplication;
    if (userType === LockedByUserType.CUSTOMER) {
      application.lockedBy = this.customer._id;
    } else if (userType === LockedByUserType.AGENT) {
      const agent = this.getAgent();
      application.lockedBy = agent._id;
    } else if (userType === LockedByUserType.TELE_INTERVIEWER) {
      application.lockedBy = LockedByUserType.TELE_INTERVIEWER;
    }
  }

  private static async getApplicationLockedBy(applicationId: string): Promise<string> {
    const resultSet = await Agent.getKnex().client.raw(
      `SELECT application."lockedBy" FROM application
        WHERE application."_id" = '${applicationId}'`
    );
    return resultSet.rowCount > 0 ? resultSet.rows[0].lockedBy : null;
  }

  @remote({ on: 'server' })
  public async isApplicationLocked(userType: LockedByUserType): Promise<boolean> {
    const application = this.customer.application as CasApplication;
    const lockedByUser = await ApplicationLockController.getApplicationLockedBy(application._id);
    if (lockedByUser) {
      //check if application is locked by current logged in user, otherwise not locked.
      let currentUser: string;
      if (userType === LockedByUserType.CUSTOMER) {
        currentUser = this.customer._id;
      } else if (userType === LockedByUserType.AGENT) {
        currentUser = this.getAgent()._id;
      } else if (userType === LockedByUserType.TELE_INTERVIEWER) {
        currentUser = LockedByUserType.TELE_INTERVIEWER;
      }
      return currentUser !== lockedByUser;
    } else {
      //application is not locked
      return false;
    }
  }
}
